/**
 * Text style presets — the typographic looks a vendor picks from instead of
 * setting twelve properties by hand.
 *
 * A preset builds one or more overlay layers (a lockup is an eyebrow plus a
 * headline; an end card is a ground, a logo, a tagline and an address).
 * Sizes are written as a fraction of the frame's SHORT side and converted to
 * the composition's height-relative unit, so a preset looks the same weight
 * in 9:16 as in 16:9.
 */
import {
    AspectRatio,
    ASPECT_SIZES,
    ImageLayer,
    OverlayLayer,
    ShapeLayer,
    TextLayer,
    createId,
} from './composition';
import type { CsBrandKit } from '../../../../common/models/content-studio.model';

export interface VideoBrand {
    brandName?: string;
    logoUrl?: string;
    /** A Google Fonts family name, when the brand has one. */
    fontFamily?: string;
    /** Text colour on light grounds. */
    ink?: string;
    /** End-card ground. */
    ground?: string;
    accent?: string;
    /** Shown on the end card. */
    website?: string;
    tagline?: string;
    /** Every brand colour, for the inspector's swatches. */
    palette?: string[];
}

export type TextPresetId =
    | 'editorial-serif'
    | 'eyebrow-headline'
    | 'minimal-caption'
    | 'bold-statement'
    | 'lower-third'
    | 'spec-tag'
    | 'quote'
    | 'tracked-title'
    | 'typewriter-note'
    | 'end-card';

export interface TextPresetMeta {
    id: TextPresetId;
    /** i18n key for the name. */
    nameKey: string;
    /** How the tile in the gallery should look (CSS). */
    sample: {
        fontFamily: string;
        fontWeight: number;
        fontStyle?: 'normal' | 'italic';
        uppercase?: boolean;
        letterSpacing?: number;
        /** Sample words shown on the tile — i18n key. */
        textKey: string;
        size: number;
        pill?: boolean;
    };
}

export const TEXT_PRESETS: TextPresetMeta[] = [
    { id: 'editorial-serif', nameKey: 'VIDEO_EDITOR.PRESET.EDITORIAL_SERIF', sample: { fontFamily: 'Cormorant Garamond', fontWeight: 500, textKey: 'VIDEO_EDITOR.SAMPLE.HEADLINE', size: 18 } },
    { id: 'eyebrow-headline', nameKey: 'VIDEO_EDITOR.PRESET.EYEBROW_HEADLINE', sample: { fontFamily: 'Playfair Display', fontWeight: 400, textKey: 'VIDEO_EDITOR.SAMPLE.HEADLINE', size: 15 } },
    { id: 'minimal-caption', nameKey: 'VIDEO_EDITOR.PRESET.MINIMAL_CAPTION', sample: { fontFamily: 'Inter', fontWeight: 400, textKey: 'VIDEO_EDITOR.SAMPLE.CAPTION', size: 12 } },
    { id: 'bold-statement', nameKey: 'VIDEO_EDITOR.PRESET.BOLD_STATEMENT', sample: { fontFamily: 'Inter', fontWeight: 800, uppercase: true, letterSpacing: -0.02, textKey: 'VIDEO_EDITOR.SAMPLE.STATEMENT', size: 17 } },
    { id: 'lower-third', nameKey: 'VIDEO_EDITOR.PRESET.LOWER_THIRD', sample: { fontFamily: 'Inter', fontWeight: 500, textKey: 'VIDEO_EDITOR.SAMPLE.PRODUCT', size: 13 } },
    { id: 'spec-tag', nameKey: 'VIDEO_EDITOR.PRESET.SPEC_TAG', sample: { fontFamily: 'Inter', fontWeight: 500, uppercase: true, letterSpacing: 0.1, textKey: 'VIDEO_EDITOR.SAMPLE.SPEC', size: 10, pill: true } },
    { id: 'quote', nameKey: 'VIDEO_EDITOR.PRESET.QUOTE', sample: { fontFamily: 'Cormorant Garamond', fontWeight: 400, fontStyle: 'italic', textKey: 'VIDEO_EDITOR.SAMPLE.QUOTE', size: 14 } },
    { id: 'tracked-title', nameKey: 'VIDEO_EDITOR.PRESET.TRACKED_TITLE', sample: { fontFamily: 'Montserrat', fontWeight: 300, uppercase: true, letterSpacing: 0.3, textKey: 'VIDEO_EDITOR.SAMPLE.COLLECTION', size: 10 } },
    { id: 'typewriter-note', nameKey: 'VIDEO_EDITOR.PRESET.TYPEWRITER', sample: { fontFamily: 'DM Mono', fontWeight: 400, textKey: 'VIDEO_EDITOR.SAMPLE.NOTE', size: 12 } },
    { id: 'end-card', nameKey: 'VIDEO_EDITOR.PRESET.END_CARD', sample: { fontFamily: 'Cormorant Garamond', fontWeight: 500, fontStyle: 'italic', textKey: 'VIDEO_EDITOR.SAMPLE.BRAND', size: 18 } },
];

export interface PresetTexts {
    /** The main line (headline, caption, quote, product name, tagline). */
    primary?: string;
    /** The second line (eyebrow, subtitle, attribution, website). */
    secondary?: string;
}

export interface PresetContext {
    aspect: AspectRatio;
    start: number;
    duration: number;
    brand?: VideoBrand;
    texts?: PresetTexts;
}

const WHITE = '#FFFFFF';
const INK = '#141412';
const SOFT_SHADOW = { color: '#000000', opacity: 0.35, blur: 0.35, offsetY: 0.04 };

/** Short-side fraction → height fraction, for this aspect. */
function sizeFor(aspect: AspectRatio, shortSide: number): number {
    const { width, height } = ASPECT_SIZES[aspect];
    return (shortSide * Math.min(width, height)) / height;
}

/** Width that reads well for a block of text in this aspect. */
function blockWidth(aspect: AspectRatio, portraitWidth: number): number {
    const { width, height } = ASPECT_SIZES[aspect];
    if (width <= height) return portraitWidth;
    // Landscape: the same physical measure is a smaller share of the width.
    return Math.min(0.9, portraitWidth * (height / width) * 1.4);
}

function text(ctx: PresetContext, patch: Partial<TextLayer>): TextLayer {
    return {
        id: createId('ly'),
        kind: 'text',
        start: ctx.start,
        duration: ctx.duration,
        x: 0.5,
        y: 0.7,
        width: blockWidth(ctx.aspect, 0.84),
        opacity: 1,
        rotation: 0,
        animIn: 'fade',
        animOut: 'fade',
        animDuration: 0.8,
        text: '',
        align: 'center',
        fontFamily: 'Inter',
        fontSize: sizeFor(ctx.aspect, 0.04),
        fontWeight: 400,
        fontStyle: 'normal',
        letterSpacing: 0,
        lineHeight: 1.2,
        color: WHITE,
        uppercase: false,
        ...patch,
    };
}

/** Shift a layer's start later while keeping its end. */
function delayed<T extends OverlayLayer>(layer: T, by: number): T {
    const d = Math.min(by, Math.max(0, layer.duration - 0.4));
    return { ...layer, start: layer.start + d, duration: layer.duration - d };
}

export function presetMeta(id: string): TextPresetMeta | undefined {
    return TEXT_PRESETS.find((p) => p.id === id);
}

/** The words a preset uses when the caller gives none. English; the UI passes translations. */
const FALLBACK: Record<TextPresetId, PresetTexts> = {
    'editorial-serif': { primary: 'Made to be lived on' },
    'eyebrow-headline': { primary: 'The new collection', secondary: 'Autumn edit' },
    'minimal-caption': { primary: 'Hand-finished, one piece at a time' },
    'bold-statement': { primary: 'Texture first' },
    'lower-third': { primary: 'Product name', secondary: 'Collection' },
    'spec-tag': { primary: '200 × 200 mm' },
    quote: { primary: '“Quiet luxury, underfoot.”' },
    'tracked-title': { primary: 'Collection' },
    'typewriter-note': { primary: 'Woven by hand' },
    'end-card': { primary: 'Designed to last', secondary: '' },
};

/**
 * Build the layers for a preset. Text the caller passes wins; otherwise the
 * fallback words above (callers in the UI always pass translated words).
 */
export function buildPresetLayers(id: TextPresetId, ctx: PresetContext): OverlayLayer[] {
    const brand = ctx.brand || {};
    const words = { ...FALLBACK[id], ...(ctx.texts || {}) };
    const serif = 'Cormorant Garamond';
    const sansFamily = 'Inter';
    const headlineSerif = brand.fontFamily && /serif|garamond|playfair|baskerville|didot|bodoni|fraunces/i.test(brand.fontFamily)
        ? brand.fontFamily
        : 'Playfair Display';

    switch (id) {
        case 'editorial-serif':
            return [text(ctx, {
                text: words.primary || '',
                fontFamily: serif,
                fontWeight: 500,
                fontSize: sizeFor(ctx.aspect, 0.11),
                lineHeight: 1.04,
                letterSpacing: -0.01,
                y: 0.70,
                animIn: 'rise',
                animOut: 'fade',
                animDuration: 1.1,
                shadow: SOFT_SHADOW,
                stylePreset: id,
            })];
        case 'eyebrow-headline': {
            const eyebrow = text(ctx, {
                text: words.secondary || '',
                fontFamily: sansFamily,
                fontWeight: 500,
                uppercase: true,
                letterSpacing: 0.32,
                fontSize: sizeFor(ctx.aspect, 0.032),
                y: 0.6,
                animIn: 'fade',
                animOut: 'fade',
                animDuration: 0.9,
                shadow: SOFT_SHADOW,
                stylePreset: id,
            });
            const headline = text(ctx, {
                text: words.primary || '',
                fontFamily: headlineSerif,
                fontWeight: 400,
                fontSize: sizeFor(ctx.aspect, 0.1),
                lineHeight: 1.08,
                letterSpacing: -0.01,
                y: 0.685,
                animIn: 'rise',
                animOut: 'fade',
                animDuration: 1.1,
                shadow: SOFT_SHADOW,
                stylePreset: id,
            });
            return [eyebrow, delayed(headline, 0.25)];
        }
        case 'minimal-caption':
            return [text(ctx, {
                text: words.primary || '',
                fontFamily: sansFamily,
                fontWeight: 400,
                fontSize: sizeFor(ctx.aspect, 0.07),
                lineHeight: 1.35,
                letterSpacing: 0.01,
                y: 0.74,
                width: blockWidth(ctx.aspect, 0.78),
                animIn: 'fade',
                animOut: 'fade',
                animDuration: 0.7,
                shadow: { ...SOFT_SHADOW, opacity: 0.45 },
                stylePreset: id,
            })];
        case 'bold-statement':
            return [text(ctx, {
                text: words.primary || '',
                fontFamily: sansFamily,
                fontWeight: 800,
                uppercase: true,
                fontSize: sizeFor(ctx.aspect, 0.13),
                lineHeight: 0.94,
                letterSpacing: -0.025,
                y: 0.5,
                width: blockWidth(ctx.aspect, 0.9),
                animIn: 'scale',
                animOut: 'blur',
                animDuration: 0.7,
                shadow: SOFT_SHADOW,
                stylePreset: id,
            })];
        case 'lower-third': {
            const w = blockWidth(ctx.aspect, 0.7);
            const left = 0.06;
            const title = text(ctx, {
                text: words.primary || '',
                fontFamily: sansFamily,
                fontWeight: 500,
                fontSize: sizeFor(ctx.aspect, 0.064),
                align: 'left',
                x: left + w / 2,
                y: 0.71,
                width: w,
                background: { style: 'box', color: INK, opacity: 0.62, paddingX: 0.55, paddingY: 0.35 },
                animIn: 'slide-left',
                animOut: 'fade',
                animDuration: 0.7,
                stylePreset: id,
            });
            const sub = text(ctx, {
                text: words.secondary || '',
                fontFamily: sansFamily,
                fontWeight: 500,
                uppercase: true,
                letterSpacing: 0.2,
                fontSize: sizeFor(ctx.aspect, 0.034),
                align: 'left',
                x: left + w / 2,
                y: 0.765,
                width: w,
                animIn: 'fade',
                animOut: 'fade',
                animDuration: 0.7,
                shadow: SOFT_SHADOW,
                stylePreset: id,
            });
            return words.secondary ? [title, delayed(sub, 0.2)] : [title];
        }
        case 'spec-tag':
            return [text(ctx, {
                text: words.primary || '',
                fontFamily: sansFamily,
                fontWeight: 500,
                uppercase: true,
                letterSpacing: 0.14,
                fontSize: sizeFor(ctx.aspect, 0.062),
                y: 0.74,
                color: INK,
                background: { style: 'pill', color: '#FAF8F4', opacity: 0.92, paddingX: 1.1, paddingY: 0.6 },
                animIn: 'scale',
                animOut: 'fade',
                animDuration: 0.6,
                stylePreset: id,
            })];
        case 'quote': {
            const q = text(ctx, {
                text: words.primary || '',
                fontFamily: serif,
                fontWeight: 400,
                fontStyle: 'italic',
                fontSize: sizeFor(ctx.aspect, 0.09),
                lineHeight: 1.12,
                y: 0.48,
                width: blockWidth(ctx.aspect, 0.82),
                animIn: 'blur',
                animOut: 'fade',
                animDuration: 1.2,
                shadow: SOFT_SHADOW,
                stylePreset: id,
            });
            if (!words.secondary) return [q];
            const by = text(ctx, {
                text: words.secondary,
                fontFamily: sansFamily,
                fontWeight: 500,
                uppercase: true,
                letterSpacing: 0.28,
                fontSize: sizeFor(ctx.aspect, 0.032),
                y: 0.62,
                animIn: 'fade',
                animOut: 'fade',
                animDuration: 0.9,
                shadow: SOFT_SHADOW,
                stylePreset: id,
            });
            return [q, delayed(by, 0.5)];
        }
        case 'tracked-title':
            return [text(ctx, {
                text: words.primary || '',
                fontFamily: 'Montserrat',
                fontWeight: 300,
                uppercase: true,
                letterSpacing: 0.42,
                fontSize: sizeFor(ctx.aspect, 0.06),
                y: 0.5,
                animIn: 'letter-stagger',
                animOut: 'fade',
                animDuration: 1.2,
                shadow: SOFT_SHADOW,
                stylePreset: id,
            })];
        case 'typewriter-note':
            return [text(ctx, {
                text: words.primary || '',
                fontFamily: 'DM Mono',
                fontWeight: 400,
                fontSize: sizeFor(ctx.aspect, 0.064),
                letterSpacing: 0.02,
                y: 0.74,
                animIn: 'typewriter',
                animOut: 'fade',
                animDuration: 1.4,
                shadow: { ...SOFT_SHADOW, opacity: 0.5 },
                stylePreset: id,
            })];
        case 'end-card':
            return buildEndCard(ctx, words);
        default:
            return [];
    }
}

function buildEndCard(ctx: PresetContext, words: PresetTexts): OverlayLayer[] {
    const brand = ctx.brand || {};
    const ground = brand.ground || '#F4F1EB';
    const ink = brand.ink || INK;
    const layers: OverlayLayer[] = [];
    const groundLayer: ShapeLayer = {
        id: createId('ly'),
        kind: 'shape',
        shape: 'rect',
        start: ctx.start,
        duration: ctx.duration,
        x: 0.5,
        y: 0.5,
        width: 1,
        height: 1,
        color: ground,
        opacity: 1,
        animIn: 'fade',
        animOut: 'none',
        animDuration: 0.6,
    };
    layers.push(groundLayer);
    const { width, height } = ASPECT_SIZES[ctx.aspect];
    const portrait = height >= width;
    if (brand.logoUrl) {
        const logo: ImageLayer = {
            id: createId('ly'),
            kind: 'image',
            src: brand.logoUrl,
            start: ctx.start,
            duration: ctx.duration,
            x: 0.5,
            y: portrait ? 0.45 : 0.42,
            width: portrait ? 0.4 : 0.22,
            opacity: 1,
            animIn: 'scale',
            animOut: 'none',
            animDuration: 1.0,
        };
        layers.push(delayed(logo, 0.2));
    } else if (brand.brandName) {
        layers.push(delayed(text(ctx, {
            text: brand.brandName,
            fontFamily: brand.fontFamily || 'Cormorant Garamond',
            fontWeight: 500,
            fontSize: sizeFor(ctx.aspect, 0.11),
            letterSpacing: 0.02,
            color: ink,
            y: portrait ? 0.45 : 0.42,
            animIn: 'rise',
            animOut: 'none',
            animDuration: 1.0,
            stylePreset: 'end-card',
        }), 0.2));
    }
    const tagline = words.primary || brand.tagline;
    if (tagline) {
        layers.push(delayed(text(ctx, {
            text: tagline,
            fontFamily: 'Cormorant Garamond',
            fontWeight: 400,
            fontStyle: 'italic',
            fontSize: sizeFor(ctx.aspect, 0.066),
            color: ink,
            y: portrait ? 0.555 : 0.56,
            animIn: 'rise',
            animOut: 'none',
            animDuration: 1.0,
            stylePreset: 'end-card',
        }), 0.55));
    }
    const site = words.secondary || brand.website;
    if (site) {
        layers.push(delayed(text(ctx, {
            text: site,
            fontFamily: 'Inter',
            fontWeight: 500,
            uppercase: true,
            letterSpacing: 0.26,
            fontSize: sizeFor(ctx.aspect, 0.03),
            color: ink,
            opacity: 0.72,
            y: portrait ? 0.615 : 0.64,
            animIn: 'fade',
            animOut: 'none',
            animDuration: 0.9,
            stylePreset: 'end-card',
        }), 0.9));
    }
    return layers;
}

/** A dark gradient behind captions, so white type reads over any footage. */
export function buildScrim(
    aspect: AspectRatio,
    start: number,
    duration: number,
    where: 'bottom' | 'top' = 'bottom',
): ShapeLayer {
    return {
        id: createId('ly'),
        kind: 'shape',
        shape: 'scrim',
        start,
        duration,
        x: 0.5,
        y: where === 'bottom' ? 0.75 : 0.25,
        width: 1,
        height: 0.5,
        color: '#000000',
        opacity: 0.55,
        direction: where === 'bottom' ? 'to-bottom' : 'to-top',
        animIn: 'fade',
        animOut: 'fade',
        animDuration: 0.6,
    };
}

export function buildShape(aspect: AspectRatio, start: number, duration: number, shape: 'rect' | 'line', color = '#FFFFFF'): ShapeLayer {
    return {
        id: createId('ly'),
        kind: 'shape',
        shape,
        start,
        duration,
        x: 0.5,
        y: 0.5,
        width: shape === 'line' ? 0.2 : 0.5,
        height: shape === 'line' ? 0.0016 : 0.2,
        radius: shape === 'rect' ? 0.02 : 0,
        color,
        opacity: shape === 'rect' ? 0.9 : 1,
        animIn: shape === 'line' ? 'wipe' : 'fade',
        animOut: 'fade',
        animDuration: 0.6,
    };
}

export function buildImageLayer(src: string, start: number, duration: number, aspect?: number): ImageLayer {
    return {
        id: createId('ly'),
        kind: 'image',
        src,
        aspect,
        start,
        duration,
        x: 0.5,
        y: 0.18,
        width: 0.3,
        opacity: 1,
        animIn: 'fade',
        animOut: 'fade',
        animDuration: 0.6,
    };
}

/** Read the brand kit into what the editor needs. */
export function brandFromKit(kit: CsBrandKit | null | undefined): VideoBrand {
    if (!kit) return {};
    const roles = kit.colorRoles || {};
    const palette = Array.from(new Set([
        ...(kit.primaryColors || []),
        ...(kit.secondaryColors || []),
        roles.ink, roles.ground, roles.accent,
    ].filter(Boolean) as string[]));
    let website: string | undefined;
    const source = kit.lastExtractedFrom || kit.sourcesRead?.[0];
    if (source) {
        try {
            const host = new URL(source.startsWith('http') ? source : `https://${source}`).hostname.replace(/^www\./, '');
            // Discovery records things like "social" here; only a domain belongs on an end card.
            if (/^[a-z0-9-]+(\.[a-z0-9-]+)+$/i.test(host)) website = host;
        } catch { /* not a url */ }
    }
    return {
        brandName: kit.brandName,
        logoUrl: kit.logoUrl,
        fontFamily: kit.primaryFont,
        ink: roles.ink || undefined,
        ground: roles.ground || undefined,
        accent: roles.accent || kit.secondaryColors?.[0],
        website,
        tagline: kit.valueProposition && kit.valueProposition.length <= 48 ? kit.valueProposition : undefined,
        palette,
    };
}
